import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { orderProcessorAPI } from '../../services/api';
import { errMsg } from './common';

const OUTSIDE = 999;
const STORE_SHORT: Record<number, string> = { 1: 'Vadasery', 2: 'KP Road', 3: 'Kottar', 4: 'Mettur' };

interface Props { open: boolean; onClose: () => void; }

/** Which FirstShield zone each distributor is located in (999 = outside all zones). Ranking suggests same-zone + 999. */
const DistributorZonesDialog: React.FC<Props> = ({ open, onClose }) => {
  const [data, setData] = useState<Awaited<ReturnType<typeof orderProcessorAPI.distributorZones>> | null>(null);
  const [q, setQ] = useState('');
  const [zoneFilter, setZoneFilter] = useState<number | 'all'>('all');
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    orderProcessorAPI.distributorZones().then(setData).catch((e) => setError(errMsg(e)));
  }, [open]);

  const zoneName = useMemo(() => new Map((data?.zones || []).map((z) => [z.id, z.id === OUTSIDE ? 'Outside FirstShield (999)' : z.name])), [data]);
  const rows = useMemo(() => (data?.distributors || [])
    .filter((d) => zoneFilter === 'all' || d.zone_id === zoneFilter)
    .filter((d) => !q || `${d.distributor_name} ${d.city || ''} ${d.pincode || ''}`.toLowerCase().includes(q.toLowerCase())), [data, q, zoneFilter]);

  const change = async (id: number, zoneId: number) => {
    setSaving(id); setError(null);
    try {
      await orderProcessorAPI.setDistributorZone(id, zoneId);
      setData((d) => d && ({ ...d, distributors: d.distributors.map((x) => (x.distributor_id === id ? { ...x, zone_id: zoneId, saved_zone_id: zoneId, source: 'manual' } : x)) }));
    } catch (e) { setError(errMsg(e)); } finally { setSaving(null); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Distributor zones</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Where each distributor is located. Distributor ranking suggests distributors of the store’s own zone and those outside all
          FirstShield zones (999); other FirstShield zones only when “Also suggest distributors from other FirstShield zones” is ticked.
          Zones were set from the address pincode/city; distributors without an address took the zone of the stores that buy from them.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {!data ? <Box sx={{ p: 3, textAlign: 'center' }}>{!error && <CircularProgress size={24} />}</Box> : (
          <>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField size="small" placeholder="Search distributor, city, pincode" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 280 }} />
              <Select size="small" value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value as any)}>
                <MenuItem value="all">All zones ({data.distributors.length})</MenuItem>
                {data.zones.map((z) => (
                  <MenuItem key={z.id} value={z.id}>{zoneName.get(z.id)} ({data.distributors.filter((d) => d.zone_id === z.id).length})</MenuItem>
                ))}
              </Select>
            </Box>
            <TableContainer sx={{ maxHeight: '60vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Distributor</TableCell><TableCell>City</TableCell><TableCell>Pincode</TableCell>
                    <TableCell align="right">Purchase lines</TableCell><TableCell>Last purchase</TableCell><TableCell>Bought by</TableCell>
                    <TableCell>Zone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((d) => (
                    <TableRow key={d.distributor_id} hover>
                      <TableCell>{d.distributor_name}</TableCell>
                      <TableCell>{d.city || '—'}</TableCell>
                      <TableCell>{d.pincode || '—'}</TableCell>
                      <TableCell align="right">{d.purchase_lines}</TableCell>
                      <TableCell>{d.last_purchase || '—'}</TableCell>
                      <TableCell>{(d.stores || []).map((s) => STORE_SHORT[s] || `#${s}`).join(', ') || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Select size="small" value={d.zone_id} disabled={saving === d.distributor_id}
                          onChange={(e) => change(d.distributor_id, Number(e.target.value))} sx={{ minWidth: 200 }}>
                          {data.zones.map((z) => <MenuItem key={z.id} value={z.id}>{zoneName.get(z.id)}</MenuItem>)}
                        </Select>
                        {d.source === 'manual' && <Chip size="small" label="edited" sx={{ ml: 1 }} />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
};

export default DistributorZonesDialog;
